import { useState } from "react";
import { motion } from "framer-motion";
import {
  FaHtml5,
  FaJs,
  FaNode,
  FaReact,
  FaGit,
  FaGithub,
  FaServer,
  FaNetworkWired,
  FaCodeBranch,
  FaPython,
  FaSpider,
  FaBug,
} from "react-icons/fa";
import {
  SiExpress,
  SiPrisma,
  SiNetlify,
  SiMysql,
  SiPostgresql,
  SiMongodb,
  SiRedis,
  SiDocker,
  SiPlaywright,
} from "react-icons/si";

// Skill category tabs
const categories = [
  { id: "all", label: "All Skills" },
  { id: "frontend", label: "Frontend" },
  { id: "backend", label: "Backend" },
  { id: "database", label: "Database" },
  { id: "tools", label: "Tools & DevOps" },
  { id: "scraping", label: "Web Scraping" },
];

// Skills data with categories and colors
const skills = [
  {
    name: "JavaScript",
    icon: FaJs,
    color: "#F7DF1E",
    textColor: "#0D0D0D",
    category: ["frontend", "backend"],
  },
  {
    name: "Python",
    icon: FaPython,
    color: "#FFD43B",
    textColor: "#306998",
    category: ["backend", "scraping"],
  },
  {
    name: "Node.js",
    icon: FaNode,
    color: "#539E43",
    textColor: "#FFFFFF",
    category: ["backend"],
  },
  {
    name: "Express",
    icon: SiExpress,
    color: "#0FFFFF",
    textColor: "#FFFFFF",
    category: ["backend"],
  },
  {
    name: "MongoDB",
    icon: SiMongodb,
    color: "#47A248",
    textColor: "#FFFFFF",
    category: ["database"],
  },
  {
    name: "Prisma",
    icon: SiPrisma,
    color: "#F7DF1E",
    textColor: "#0D0D0D",
    category: ["backend", "database"],
  },
  {
    name: "MySQL",
    icon: SiMysql,
    color: "#FF79A1",
    textColor: "#FFFFFF",
    category: ["database"],
  },
  {
    name: "PostgreSQL",
    icon: SiPostgresql,
    color: "#336791",
    textColor: "#FFFFFF",
    category: ["database"],
  },
  {
    name: "Redis",
    icon: SiRedis,
    color: "#DC382D",
    textColor: "#FFFFFF",
    category: ["database"],
  },
  {
    name: "HTML5",
    icon: FaHtml5,
    color: "#E34F26",
    textColor: "#FFFFFF",
    category: ["frontend"],
  },
  {
    name: "React",
    icon: FaReact,
    color: "#61DAFB",
    textColor: "#0D0D0D",
    category: ["frontend"],
  },
  {
    name: "Scrapy",
    icon: FaSpider,
    color: "#60A839",
    textColor: "#FFFFFF",
    category: ["scraping", "tools"],
  },
  {
    name: "Crawlee",
    icon: FaBug,
    color: "#FF6B6B",
    textColor: "#FFFFFF",
    category: ["scraping", "tools"],
  },
  {
    name: "Playwright",
    icon: SiPlaywright,
    color: "#2EAD33",
    textColor: "#FFFFFF",
    category: ["tools", "scraping"],
  },
  {
    name: "Git",
    icon: FaGit,
    color: "#F05032",
    textColor: "#FFFFFF",
    category: ["tools"],
  },
  {
    name: "GitHub",
    icon: FaGithub,
    color: "#FFF98F",
    textColor: "#FFFFFF",
    category: ["tools"],
  },
  {
    name: "Docker",
    icon: SiDocker,
    color: "#2496ED",
    textColor: "#FFFFFF",
    category: ["tools"],
  },

  {
    name: "Netlify",
    icon: SiNetlify,
    color: "#00C7B7",
    textColor: "#FFFFFF",
    category: ["tools"],
  },
  {
    name: "RESTful APIs",
    icon: FaNetworkWired,
    color: "#6236FF",
    textColor: "#FFFFFF",
    category: ["backend"],
  },
  {
    name: "CI/CD",
    icon: FaCodeBranch,
    color: "#43A047",
    textColor: "#FFFFFF",
    category: ["tools"],
  },
  {
    name: "Microservices",
    icon: FaServer,
    color: "#FF4081",
    textColor: "#FFFFFF",
    category: ["backend"],
  },
];

// Individual skill card component
const SkillCard = ({ skill, index }) => {
  const Icon = skill.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="h-full"
    >
      <motion.div
        className="glass-card rounded-xl p-6 h-full flex flex-col items-center justify-center border border-primary-500/10 hover:border-primary-500/30 transition-all duration-300"
      >
        <div
          className="w-16 h-16 flex items-center justify-center rounded-lg mb-4"
          style={{
            backgroundColor: `${skill.color}20`,
            color: skill.color,
          }}
        >
          <Icon className="w-8 h-8" />
        </div>
        <h3 className="text-lg font-medium text-light-100 mb-1">
          {skill.name}
        </h3>

        {/* Skill proficiency visualization */}
        <div className="w-full mt-3 bg-dark-800 rounded-full h-1.5">
          <div
            className="h-1.5 rounded-full"
            style={{
              width: `${75 + Math.random() * 25}%`,
              background: `linear-gradient(90deg, ${skill.color}80, ${skill.color})`,
            }}
          ></div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Category tab buttons
const CategoryButton = ({ category, activeCategory, onClick }) => {
  const isActive = activeCategory === category.id;

  return (
    <motion.button
      onClick={() => onClick(category.id)}
      className={`py-2 px-4 rounded-lg text-sm font-medium transition-all relative overflow-hidden ${
        isActive ? "text-dark-900" : "text-gray-400 hover:text-primary-400"
      }`}
    >
      {isActive && (
        <motion.span
          className="absolute inset-0 bg-primary-500"
          layoutId="activeSkillCategory"
          initial={false}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      )}
      <span className="relative z-10">{category.label}</span>
    </motion.button>
  );
};

// Main Skills component
const Skills = ({ standalone = false }) => {
  const HeadingTag = standalone ? "h1" : "h2";
  const [activeCategory, setActiveCategory] = useState("all");

  // Filter skills based on active category
  const filteredSkills =
    activeCategory === "all"
      ? skills
      : skills.filter((skill) => skill.category.includes(activeCategory));

  return (
    <section className="py-20 bg-dark-900/40 text-light-100 relative overflow-hidden">
      {/* Background elements */}

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="mb-12 max-w-3xl"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="font-code text-sm text-primary-400 mb-3 block">
            My Expertise
          </span>
          <HeadingTag className="text-4xl font-bold gradient-text mb-4">
            {standalone
              ? "Backend Developer & Web Scraper Skills in Nepal"
              : "Technical Skills"}
          </HeadingTag>
          <p className="text-lg text-gray-400 max-w-2xl">
            A showcase of my technical abilities and expertise in various
            technologies
          </p>
        </motion.div>

        {/* Category filter buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {categories.map((category) => (
            <CategoryButton
              key={category.id}
              category={category}
              activeCategory={activeCategory}
              onClick={setActiveCategory}
            />
          ))}
        </div>

        {/* Skills grid */}
        <motion.div
          layout
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
        >
          {filteredSkills.map((skill, index) => (
            <SkillCard key={skill.name} skill={skill} index={index} />
          ))}
        </motion.div>

        {/* Interactive element - Skill meter */}
        <motion.div
          className="mt-16 p-8 glass-card rounded-lg border border-primary-500/10 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <h3 className="text-2xl font-bold gradient-text mb-4">
            Skill Development Focus
          </h3>
          <p className="text-gray-400 mb-6">
            I'm continuously sharpening my web scraping and backend expertise —
            staying ahead of anti-bot technologies, scaling data pipelines, and
            delivering reliable automated data extraction solutions.
          </p>

          <div className="space-y-6">
            {[
              { name: "Large-Scale Web Scraping & Crawling", progress: 90 },
              { name: "Anti-Bot Bypass & Proxy Rotation", progress: 85 },
              { name: "Data Pipeline & ETL Automation", progress: 80 },
              { name: "Backend API Development (Node.js)", progress: 88 },
              {
                name: "Headless Browser Automation (Puppeteer / Playwright)",
                progress: 85,
              },
            ].map((skill, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-light-100">{skill.name}</span>
                  <span className="text-primary-400">{skill.progress}%</span>
                </div>
                <div className="w-full bg-dark-800 rounded-full h-2.5 overflow-hidden">
                  <motion.div
                    className="h-2.5 rounded-full bg-gradient-to-r from-primary-500 to-pink-500"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${skill.progress}%` }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 1,
                      ease: "easeOut",
                      delay: 0.2 + index * 0.2,
                    }}
                  ></motion.div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <motion.a
              href="/webscraper"
              className="inline-flex items-center px-6 py-3 rounded-lg bg-dark-800 text-primary-400 font-medium border border-primary-500/40 hover:bg-dark-700 transition-colors"
            >
              <span>View Scraping Services</span>
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                ></path>
              </svg>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
